import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiSearch, FiUser, FiChevronDown, FiCheck, FiX, FiMapPin, FiPlus, FiArrowLeft, FiMail, FiPhone, FiHome, FiHash, FiBox } from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';
import iaaiIcon from '../../assets/images/iaai icon@0.5x.png';

/* Green Rate M icon */
const RateMIcon: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 22 22" fill="none">
    <path d="M9.63 13.16c0-1.55.01-3.1 0-4.65-.01-.83.3-1.52.84-2.11.94-1.02 1.92-2 2.83-3.03.46-.52 1.69-.47 2.01.31.06.14.1.3.1.45.01 3.08.01 6.15 0 9.23-.01.79-.38 1.46-.9 2.03-.98 1.08-1.99 2.13-3.01 3.17-.5.51-1.22.58-1.63.14-.16-.18-.23-.5-.24-.76-.02-1.59-.01-3.18-.01-4.77z" fill="#00966E"/>
    <path d="M21.91 8.78c0 1.5-.01 3-.01 4.5 0 .71-.23 1.33-.71 1.84-1.05 1.12-2.09 2.25-3.17 3.35-.23.24-.58.44-.9.49-.55.09-1.01-.4-1.01-1.02 0-3.17 0-6.34.02-9.5 0-.78.29-1.45.85-2.03.9-.94 1.78-1.9 2.65-2.87.42-.47.9-.68 1.52-.46.5.17.75.51.75 1.04v4.65z" fill="#00966E"/>
    <path d="M8.87 12.29c-.08 2.46.09 2-.4 3.45-.81.88-1.63 1.76-2.46 2.62-.22.23-.49.45-.78.56-.64.26-1.28-.19-1.29-.89-.01-.8-.04-1.59.02-2.38.02-.32.18-.69.4-.93 1.25-1.39 2.54-2.75 3.82-4.13.25-.27.51-.48.9-.46.45.02.71.29.72.75 0 .52 0 1.04 0 1.41z" fill="#00966E"/>
  </svg>
);

/* Inline SVG icons for LOBs */
const HardHatIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12V16H4V12C4 7.58 7.58 4 12 4C16.42 4 20 7.58 20 12V16H22V12C22 6.48 17.52 2 12 2ZM12 6C8.69 6 6 8.69 6 12V16H8V12C8 9.79 9.79 8 12 8C14.21 8 16 9.79 16 12V16H18V12C18 8.69 15.31 6 12 6ZM10 16V20H14V16H10Z"/></svg>
);
const CyberIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 1L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 1ZM12 11.99H19C18.47 16.11 15.72 19.78 12 20.93V12H5V6.3L12 3.19V11.99Z"/></svg>
);
const GLIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L4 5V11.09C4 16.14 7.41 20.85 12 22C16.59 20.85 20 16.14 20 11.09V5L12 2ZM18 11.09C18 15.09 15.45 18.79 12 19.92C8.55 18.79 6 15.1 6 11.09V6.39L12 4.14L18 6.39V11.09ZM8.82 10.59L7.4 12L11 15.59L16.59 10L15.17 8.59L11 12.76L8.82 10.59Z"/></svg>
);
const BOPIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 7V3H2V21H22V7H12ZM6 19H4V17H6V19ZM6 15H4V13H6V15ZM6 11H4V9H6V11ZM6 7H4V5H6V7ZM10 19H8V17H10V19ZM10 15H8V13H10V15ZM10 11H8V9H10V11ZM10 7H8V5H10V7ZM20 19H12V17H14V15H12V13H14V11H12V9H20V19ZM18 11H16V13H18V11ZM18 15H16V17H18V15Z"/></svg>
);

/* ── Types ─────────────────────────────────────────────────────────────────── */

interface NowCertsClient {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  cellPhone: string | null;
  addressLine1: string | null;
  city: string | null;
  stateName: string | null;
  stateAbv: string | null;
  zipCode: string | null;
  personalType_FirstName: string | null;
  personalType_LastName: string | null;
  type: number;
  typeName: string | null;
}

type CommercialLOB = 'WC' | 'Cyber' | 'GL' | 'BOP';
type ViewMode = 'search' | 'create';

interface CLRClassicQuoteTabProps {
  onStartQuote: (opts: {
    clientId: string;
    clientName: string;
    lob: CommercialLOB;
    state: string;
  }) => void;
  onSwitchToImport?: () => void;
}

/* ── Constants ─────────────────────────────────────────────────────────────── */

const PROSPECTS_BASE = `${process.env.REACT_APP_Auth_Url || 'https://momentumampapi-stage.insurancegig.com'}/api/AMPNowcerts`;

const US_STATES = [
  { name: 'Alabama', code: 'AL' }, { name: 'Alaska', code: 'AK' }, { name: 'Arizona', code: 'AZ' },
  { name: 'Arkansas', code: 'AR' }, { name: 'California', code: 'CA' }, { name: 'Colorado', code: 'CO' },
  { name: 'Connecticut', code: 'CT' }, { name: 'Delaware', code: 'DE' }, { name: 'Florida', code: 'FL' },
  { name: 'Georgia', code: 'GA' }, { name: 'Hawaii', code: 'HI' }, { name: 'Idaho', code: 'ID' },
  { name: 'Illinois', code: 'IL' }, { name: 'Indiana', code: 'IN' }, { name: 'Iowa', code: 'IA' },
  { name: 'Kansas', code: 'KS' }, { name: 'Kentucky', code: 'KY' }, { name: 'Louisiana', code: 'LA' },
  { name: 'Maine', code: 'ME' }, { name: 'Maryland', code: 'MD' }, { name: 'Massachusetts', code: 'MA' },
  { name: 'Michigan', code: 'MI' }, { name: 'Minnesota', code: 'MN' }, { name: 'Mississippi', code: 'MS' },
  { name: 'Missouri', code: 'MO' }, { name: 'Montana', code: 'MT' }, { name: 'Nebraska', code: 'NE' },
  { name: 'Nevada', code: 'NV' }, { name: 'New Hampshire', code: 'NH' }, { name: 'New Jersey', code: 'NJ' },
  { name: 'New Mexico', code: 'NM' }, { name: 'New York', code: 'NY' }, { name: 'North Carolina', code: 'NC' },
  { name: 'North Dakota', code: 'ND' }, { name: 'Ohio', code: 'OH' }, { name: 'Oklahoma', code: 'OK' },
  { name: 'Oregon', code: 'OR' }, { name: 'Pennsylvania', code: 'PA' }, { name: 'Rhode Island', code: 'RI' },
  { name: 'South Carolina', code: 'SC' }, { name: 'South Dakota', code: 'SD' }, { name: 'Tennessee', code: 'TN' },
  { name: 'Texas', code: 'TX' }, { name: 'Utah', code: 'UT' }, { name: 'Vermont', code: 'VT' },
  { name: 'Virginia', code: 'VA' }, { name: 'Washington', code: 'WA' }, { name: 'West Virginia', code: 'WV' },
  { name: 'Wisconsin', code: 'WI' }, { name: 'Wyoming', code: 'WY' }, { name: 'District of Columbia', code: 'DC' },
];

const LOB_OPTIONS: { type: CommercialLOB; label: string; hint: string; icon: React.ReactNode; color: string }[] = [
  { type: 'WC', label: "Workers' Comp", hint: 'Employer Liability', icon: <HardHatIcon size={20} />, color: '#f59e0b' },
  { type: 'GL', label: 'General Liability', hint: 'Commercial GL', icon: <GLIcon size={20} />, color: '#3b82f6' },
  { type: 'BOP', label: 'Business Owners', hint: 'Property & Liability', icon: <BOPIcon size={20} />, color: '#8b5cf6' },
  { type: 'Cyber', label: 'Cyber Policy', hint: 'Data Breach & Liability', icon: <CyberIcon size={20} />, color: '#10b981' },
];

/* ── Component ─────────────────────────────────────────────────────────────── */

const CLRClassicQuoteTab: React.FC<CLRClassicQuoteTabProps> = ({ onStartQuote, onSwitchToImport }) => {
  /* ---- View mode ---- */
  const [viewMode, setViewMode] = useState<ViewMode>('search');

  /* ---- Client search ---- */
  const [clientSearch, setClientSearch] = useState('');
  const [clients, setClients] = useState<NowCertsClient[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<NowCertsClient | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* ---- Form state ---- */
  const [lob, setLob] = useState<CommercialLOB | null>(null);
  const [stateCode, setStateCode] = useState('');
  const [stateSearch, setStateSearch] = useState('');
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  const stateRef = useRef<HTMLDivElement>(null);

  /* ---- Create New Client ---- */
  const [createForm, setCreateForm] = useState({ commercialName: '', email: '', phoneNumber: '', addressLine1: '', addressLine2: '', city: '', stateName: '', zipCode: '', dba: '', naicsCode: '' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  /* ---- Submission ---- */
  const [submitting, setSubmitting] = useState(false);

  /* ---- Client search API ---- */
  const searchClients = useCallback(async (query: string) => {
    if (query.trim().length < 2) { setClients([]); return; }
    const authToken = localStorage.getItem('authToken');
    const ncToken = localStorage.getItem('amsToken') || '';
    if (!authToken || !ncToken) return;
    setClientsLoading(true);
    try {
      const resp = await fetch(
        `${PROSPECTS_BASE}/client-search?search=${encodeURIComponent(query.trim())}&type=commercial`,
        { headers: { Authorization: `Bearer ${authToken}`, NCToken: ncToken } },
      );
      if (!resp.ok) { setClients([]); return; }
      const json = await resp.json();
      setClients(Array.isArray(json?.clients) ? json.clients : []);
    } catch {
      setClients([]);
    } finally {
      setClientsLoading(false);
    }
  }, []);

  /* Debounced search on keystroke */
  useEffect(() => {
    if (selectedClient) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (clientSearch.trim().length < 2) { setClients([]); setShowDropdown(false); return; }
    debounceRef.current = setTimeout(() => {
      searchClients(clientSearch);
      setShowDropdown(true);
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [clientSearch, selectedClient, searchClients]);

  /* Close dropdowns on outside click */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setShowDropdown(false);
      if (stateRef.current && !stateRef.current.contains(e.target as Node)) setStateDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* Auto-fill state from selected client */
  const selectClient = useCallback((c: NowCertsClient) => {
    setSelectedClient(c);
    setClientSearch(c.name);
    setShowDropdown(false);
    setViewMode('search');
    if (c.stateAbv) {
      setStateCode(c.stateAbv);
      const match = US_STATES.find(s => s.code === c.stateAbv);
      setStateSearch(match ? match.name : c.stateName || c.stateAbv);
    }
  }, []);

  const clearClient = useCallback(() => {
    setSelectedClient(null);
    setClientSearch('');
    setClients([]);
  }, []);

  /* ---- State filtering ---- */
  const filteredStates = stateSearch
    ? US_STATES.filter(s =>
        s.name.toLowerCase().includes(stateSearch.toLowerCase()) ||
        s.code.toLowerCase().includes(stateSearch.toLowerCase()))
    : US_STATES;

  /* ---- Validation ---- */
  const canSubmit = selectedClient && lob && stateCode && !submitting;

  /* ---- Submit ---- */
  const handleSubmit = useCallback(() => {
    if (!selectedClient || !lob || !stateCode) return;
    setSubmitting(true);
    onStartQuote({
      clientId: selectedClient.id,
      clientName: selectedClient.name,
      lob,
      state: stateCode,
    });
    setTimeout(() => setSubmitting(false), 1500);
  }, [selectedClient, lob, stateCode, onStartQuote]);

  /* ---- Create New Client ---- */
  const handleCreateClient = useCallback(async () => {
    if (!createForm.commercialName.trim()) { setCreateError('Commercial name is required.'); return; }
    setCreating(true); setCreateError('');
    const authToken = localStorage.getItem('authToken');
    const ncToken = localStorage.getItem('amsToken') || '';
    try {
      const resp = await fetch(`${PROSPECTS_BASE}/client-insert`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}`, NCToken: ncToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...createForm, type: 'Commercial' }),
      });
      if (!resp.ok) { const err = await resp.json().catch(() => ({})); setCreateError((err as any).error || 'Failed to create client.'); return; }
      const json = await resp.json();
      if (json.success && json.client) {
        selectClient(json.client as NowCertsClient);
        setCreateForm({ commercialName: '', email: '', phoneNumber: '', addressLine1: '', addressLine2: '', city: '', stateName: '', zipCode: '', dba: '', naicsCode: '' });
      } else { setCreateError('Unexpected response from server.'); }
    } catch { setCreateError('Network error. Please try again.'); }
    finally { setCreating(false); }
  }, [createForm, selectClient]);

  /* ---- Render ---- */
  return (
    <div className="cq-tab">
      <div className="cq-card">
        <div className="cq-card__header">
          <div className="cq-card__logos">
            <RateMIcon size={26} />
            <span className="cq-card__logo-x">x</span>
            <img src={iaaiIcon} alt="InsuranceAgency.AI" className="cq-card__logo-icon" />
          </div>
          <h2 className="cq-card__title">AI-Powered Commercial Lines Quote</h2>
          <p className="cq-card__subtitle">Select your client, choose a line of business, and let AI handle the rest — compare rates across carriers in seconds.</p>
        </div>

        {viewMode === 'search' ? (
          /* ══════════════ SEARCH VIEW ══════════════ */
          <div className="cq-form">
            {/* ── Client Name ── */}
            <div className="cq-field">
              <label className="cq-field__label">
                Client Name <span className="cq-field__req">*</span>
              </label>
              <div className="cq-field__row">
                <div className="cq-search-wrap" ref={searchRef} style={{ flex: 1 }}>
                  <FiSearch size={14} className="cq-search-wrap__icon" />
                  <input
                    type="text"
                    className="cq-input cq-input--search"
                    placeholder="Search for an Existing Client"
                    value={clientSearch}
                    onChange={e => {
                      setClientSearch(e.target.value);
                      if (selectedClient) setSelectedClient(null);
                    }}
                    onFocus={() => { if (clients.length > 0 && !selectedClient) setShowDropdown(true); }}
                  />
                  {selectedClient && (
                    <button type="button" className="cq-search-wrap__clear" onClick={clearClient} title="Clear selection">
                      <FiX size={14} />
                    </button>
                  )}

                  {/* Dropdown */}
                  {showDropdown && (
                    <div className="cq-dropdown">
                      {clientsLoading ? (
                        <div className="cq-dropdown__loading">
                          <div className="cq-spinner" /> Searching...
                        </div>
                      ) : clients.length === 0 ? (
                        <div className="cq-dropdown__empty">No clients found for &ldquo;{clientSearch}&rdquo;</div>
                      ) : (
                        <ul className="cq-dropdown__list">
                          {clients.map(c => (
                            <li key={c.id} className="cq-dropdown__item" onClick={() => selectClient(c)}>
                              <div className="cq-dropdown__item-icon">
                                <FiUser size={14} />
                              </div>
                              <div className="cq-dropdown__item-info">
                                <span className="cq-dropdown__item-name">{c.name}</span>
                                <span className="cq-dropdown__item-detail">
                                  {[c.city, c.stateName || c.stateAbv].filter(Boolean).join(', ')}
                                  {c.typeName ? ` \u00B7 ${c.typeName}` : ''}
                                </span>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
                <button type="button" className="cq-btn cq-btn--outline cq-btn--create" onClick={() => { setViewMode('create'); setCreateError(''); }}>
                  <FiPlus size={14} /> Create New Client
                </button>
              </div>
            </div>

            {/* ── Line of Business ── */}
            <div className="cq-field">
              <label className="cq-field__label">
                Line of Business <span className="cq-field__req">*</span>
              </label>
              <div className="cq-lob-grid cq-lob-grid--4">
                {LOB_OPTIONS.map(opt => (
                  <button
                    key={opt.type}
                    type="button"
                    className={`cq-lob-card${lob === opt.type ? ' cq-lob-card--active' : ''}`}
                    onClick={() => setLob(opt.type)}
                    style={{ '--lob-color': opt.color } as React.CSSProperties}
                  >
                    <div className="cq-lob-card__icon">{opt.icon}</div>
                    <div className="cq-lob-card__text">
                      <span className="cq-lob-card__label">{opt.label}</span>
                      <span className="cq-lob-card__hint">{opt.hint}</span>
                    </div>
                    {lob === opt.type && (
                      <div className="cq-lob-card__check"><FiCheck size={14} /></div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* ── State ── */}
            <div className="cq-field">
              <label className="cq-field__label">
                State <span className="cq-field__req">*</span>
              </label>
              <div className="cq-select-wrap" ref={stateRef}>
                <FiMapPin size={14} className="cq-select-wrap__icon" />
                <input
                  type="text"
                  className="cq-input cq-input--select"
                  placeholder="Start typing to Search State"
                  value={stateSearch}
                  onChange={e => {
                    setStateSearch(e.target.value);
                    setStateCode('');
                    setStateDropdownOpen(true);
                  }}
                  onFocus={() => setStateDropdownOpen(true)}
                />
                <FiChevronDown size={14} className="cq-select-wrap__caret" />
                {stateDropdownOpen && (
                  <div className="cq-dropdown cq-dropdown--state">
                    {filteredStates.length === 0 ? (
                      <div className="cq-dropdown__empty">No matching state</div>
                    ) : (
                      <ul className="cq-dropdown__list">
                        {filteredStates.map(s => (
                          <li
                            key={s.code}
                            className={`cq-dropdown__item${stateCode === s.code ? ' cq-dropdown__item--selected' : ''}`}
                            onClick={() => { setStateCode(s.code); setStateSearch(s.name); setStateDropdownOpen(false); }}
                          >
                            <span>{s.name}</span>
                            <span className="cq-dropdown__item-code">{s.code}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* ── Selected Client Summary ── */}
            {selectedClient && (
              <div className="cq-client-summary">
                <div className="cq-client-summary__avatar">
                  <FiUser size={18} />
                </div>
                <div className="cq-client-summary__info">
                  <span className="cq-client-summary__name">{selectedClient.name}</span>
                  <span className="cq-client-summary__detail">
                    {[selectedClient.addressLine1, selectedClient.city, selectedClient.stateName || selectedClient.stateAbv, selectedClient.zipCode].filter(Boolean).join(', ')}
                  </span>
                  {selectedClient.email && (
                    <span className="cq-client-summary__detail">{selectedClient.email}</span>
                  )}
                  {(selectedClient.phone || selectedClient.cellPhone) && (
                    <span className="cq-client-summary__detail">{selectedClient.phone || selectedClient.cellPhone}</span>
                  )}
                </div>
                <span className={`cq-client-summary__badge cq-client-summary__badge--${selectedClient.typeName?.toLowerCase() || 'unknown'}`}>
                  {selectedClient.typeName || 'Client'}
                </span>
              </div>
            )}
          </div>
        ) : (
          /* ══════════════ CREATE NEW CLIENT VIEW ══════════════ */
          <div className="cq-form">
            <div className="cq-create-header">
              <button type="button" className="cq-create-header__back" onClick={() => setViewMode('search')}>
                <FiArrowLeft size={16} />
              </button>
              <div>
                <h3 className="cq-create-header__title">Create New Client</h3>
                <p className="cq-create-header__subtitle">Add a new commercial client to NowCerts AMS and start quoting instantly.</p>
              </div>
            </div>

            {createError && <div className="cq-create-error">{createError}</div>}

            <div className="cq-create-grid">
              <div className="cq-create-field">
                <label>Commercial Name <span className="cq-field__req">*</span></label>
                <div className="cq-create-field__input-wrap">
                  <FiBox size={14} className="cq-create-field__icon" />
                  <input className="cq-input cq-input--icon" placeholder="Enter Business Name" value={createForm.commercialName} onChange={e => setCreateForm(f => ({ ...f, commercialName: e.target.value }))} />
                </div>
              </div>
              <div className="cq-create-field">
                <label>NAICS Code</label>
                <div className="cq-create-field__input-wrap">
                  <FiHash size={14} className="cq-create-field__icon" />
                  <input className="cq-input cq-input--icon" placeholder="Start typing NAICS code" value={createForm.naicsCode} onChange={e => setCreateForm(f => ({ ...f, naicsCode: e.target.value }))} />
                </div>
              </div>
              <div className="cq-create-field">
                <label>DBA (Doing Business As)</label>
                <div className="cq-create-field__input-wrap">
                  <FiBox size={14} className="cq-create-field__icon" />
                  <input className="cq-input cq-input--icon" placeholder="Enter DBA (optional)" value={createForm.dba} onChange={e => setCreateForm(f => ({ ...f, dba: e.target.value }))} />
                </div>
              </div>
              <div className="cq-create-field">
                <label>Email Address</label>
                <div className="cq-create-field__input-wrap">
                  <FiMail size={14} className="cq-create-field__icon" />
                  <input className="cq-input cq-input--icon" type="email" placeholder="Enter email address" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} />
                </div>
              </div>
              <div className="cq-create-field">
                <label>Phone Number</label>
                <div className="cq-create-field__input-wrap">
                  <FiPhone size={14} className="cq-create-field__icon" />
                  <input className="cq-input cq-input--icon" placeholder="Enter phone number" value={createForm.phoneNumber} onChange={e => setCreateForm(f => ({ ...f, phoneNumber: e.target.value }))} />
                </div>
              </div>
              <div className="cq-create-field">
                <label>Address Line 1</label>
                <div className="cq-create-field__input-wrap">
                  <FiHome size={14} className="cq-create-field__icon" />
                  <input className="cq-input cq-input--icon" placeholder="Street address" value={createForm.addressLine1} onChange={e => setCreateForm(f => ({ ...f, addressLine1: e.target.value }))} />
                </div>
              </div>
              <div className="cq-create-field">
                <label>Address Line 2</label>
                <div className="cq-create-field__input-wrap">
                  <FiHome size={14} className="cq-create-field__icon" />
                  <input className="cq-input cq-input--icon" placeholder="Apt, suite, unit (optional)" value={createForm.addressLine2} onChange={e => setCreateForm(f => ({ ...f, addressLine2: e.target.value }))} />
                </div>
              </div>
              <div className="cq-create-field">
                <label>City</label>
                <div className="cq-create-field__input-wrap">
                  <FiMapPin size={14} className="cq-create-field__icon" />
                  <input className="cq-input cq-input--icon" placeholder="Enter city" value={createForm.city} onChange={e => setCreateForm(f => ({ ...f, city: e.target.value }))} />
                </div>
              </div>
              <div className="cq-create-field">
                <label>State</label>
                <div className="cq-create-field__input-wrap">
                  <FiMapPin size={14} className="cq-create-field__icon" />
                  <select className="cq-input cq-input--icon" value={createForm.stateName} onChange={e => setCreateForm(f => ({ ...f, stateName: e.target.value }))}>
                    <option value="">Select state</option>
                    {US_STATES.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="cq-create-field">
                <label>Zip Code</label>
                <div className="cq-create-field__input-wrap">
                  <FiHash size={14} className="cq-create-field__icon" />
                  <input className="cq-input cq-input--icon" placeholder="Enter zip code" value={createForm.zipCode} onChange={e => setCreateForm(f => ({ ...f, zipCode: e.target.value }))} />
                </div>
              </div>
            </div>

            {/* ── Import Promo ── */}
            <div className="cq-import-promo">
              <div className="cq-import-promo__icon"><RiSparklingFill size={20} /></div>
              <div className="cq-import-promo__text">
                <strong>Have a Dec Page or policy document?</strong>
                <span>Skip manual entry — upload your PDF, ACORD form, or XML and InsuranceAgency.AI will extract all client details, create the prospect, and quote across carriers automatically.</span>
              </div>
              {onSwitchToImport && (
                <button type="button" className="cq-btn cq-btn--import-promo" onClick={onSwitchToImport}>
                  <RiSparklingFill size={13} /> Go to Import
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Action Buttons ── */}
        <div className="cq-actions">
          {viewMode === 'create' ? (
            <>
              <button type="button" className="cq-btn cq-btn--ghost" onClick={() => setViewMode('search')}>Cancel</button>
              <button type="button" className="cq-btn cq-btn--primary" disabled={creating || !createForm.commercialName.trim()} onClick={handleCreateClient}>
                {creating ? <><div className="cq-spinner cq-spinner--white" /> Creating...</> : <><FiPlus size={14} /> Create Client</>}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="cq-btn cq-btn--primary"
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              {submitting ? (
                <><div className="cq-spinner cq-spinner--white" /> Starting...</>
              ) : (
                <><RiSparklingFill size={15} /> Start AI Quote</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CLRClassicQuoteTab;
